import clsx from "clsx";
import { graphql } from "gatsby";
import React, { FC } from "react";

import { usePageFullPath } from "../../../hooks/use-page-full-path";
import { useRenderDateRange } from "../../../hooks/use-render-date-range";
import { MaterialType } from "../../../types/materials";
import { Pill } from "../../forms";
import { TagColors, TagDimensions } from "../../forms/elements/tags/types";
import { Icon } from "../../icon";

export interface ExhibitionCardProps extends Queries.ExhibitionCardDataFragment {
  labels: {
    [MaterialType.Wood]: string;
    [MaterialType.Stone]: string;
    [MaterialType.Glass]: string;
    [MaterialType.Materia]: string;
    [MaterialType.Metal]: string;
    "exhibition-card-location": string;
  };
  fullWidth?: boolean;
}

const commonDatesClasses = "uppercase text-lg p-2 text-center";

export const ExhibitionCard: FC<ExhibitionCardProps> = ({
  startDate: startDateAsString,
  endDate: endDateAsString,
  materials,
  title,
  labels,
  fullWidth,
  location,
  standardpage,
}) => {
  const [dateLine1, dateLine2] = useRenderDateRange({
    startDateAsString,
    endDateAsString,
  });

  const pageId = standardpage?.[0]?.id;
  const pageUrl = usePageFullPath(pageId);

  const innerComponent = (
    <div
      className={clsx(
        "rounded-2xl overflow-hidden flex bg-primary-light",
        fullWidth ? "w-full h-full" : "w-fit",
        "hover:shadow-lg transition-all duration-300 cursor-pointer"
      )}
    >
      <div className="flex flex-col flex-none">
        <div
          className={clsx(
            "bg-primary flex-none text-light py-4 px-3",
            "w-[8ch] flex items-center justify-center",
            commonDatesClasses
          )}
        >
          {dateLine1}
        </div>
        <div className={clsx("text-dark font-bold py-4 px-3", commonDatesClasses)}>{dateLine2}</div>
      </div>
      <div className="flex-1 bg-light py-3 px-4 flex flex-col gap-2">
        {materials && (
          <div className="flex items-center gap-2 pb-1 flex-wrap">
            {materials.map(
              (material) =>
                material && (
                  <Pill
                    checked
                    readOnly
                    dimension={TagDimensions.Small}
                    color={material as TagColors}
                    key={material}
                  >
                    {labels[material as MaterialType]}
                  </Pill>
                )
            )}
          </div>
        )}

        {title && <h3 className="text-2xl flex-1 text-primary font-medium">{title}</h3>}

        <div className="flex items-center w-full">
          <div className="flex-1 flex items-center">
            {location && (
              <div>
                <span className="text-primary font-bold">
                  {labels["exhibition-card-location"]}&nbsp;
                </span>
                <span>{location}</span>
              </div>
            )}
          </div>
          <Icon icon="arrow-right" className="h-5 flex-none text-primary" />
        </div>
      </div>
    </div>
  );

  return pageUrl ? <a href={pageUrl}> {innerComponent} </a> : innerComponent;
};

export const query = graphql`
  fragment ExhibitionCardData on ContentfulExhibition {
    id
    title
    node_locale
    ticketPurchaseLink {
      ...CtaData
    }
    location
    materials
    startDate
    endDate
    standardpage {
      id
    }
  }
`;
